import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { getDoctorsAction } from "../../../store/actions/DoctorsAction";
import { markEmergencyAction } from "../../../store/actions/ServiceCallerAction";

const EmergencyModal = ({ show, onClose, patient, onSubmit }) => {
  const dispatch = useDispatch();

  const doctors = useSelector((state) => state?.doctors?.doctors || []);

  const [loading, setLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [message, setMessage] = useState("");

  /* ---------------- LOAD DOCTORS ---------------- */
  useEffect(() => {
    if (show) {
      dispatch(getDoctorsAction());
    }
  }, [dispatch, show]);
  
  /* ✅ SET DEFAULT DOCTOR FROM API RESPONSE (emergencyMark_doctor_id / doctor_id) */
  useEffect(() => {
    if (patient && doctors.length > 0) {
      const doctorIdToUse = patient?.emergencyMark_doctor_id || patient?.doctor_id;
      
      if (doctorIdToUse && String(doctorIdToUse) !== "0") {
        const matchedDoctor = doctors.find(
          (doc) => String(doc.doctor_id || doc.id) === String(doctorIdToUse)
        );
    
        if (matchedDoctor) {
          console.log("[v0] Setting default doctor:", matchedDoctor);
          setSelectedDoctor({
            value: matchedDoctor.doctor_id || matchedDoctor.id,
            label: matchedDoctor.doctor_name || matchedDoctor.name,
          });
        }
      }
    }
  }, [patient, doctors]);

  /* ---------------- AUTO MESSAGE ---------------- */
  useEffect(() => {
    if (patient) {
      setMessage(
        `🚨 *EMERGENCY ALERT* 🚨\n\n` +
          `Patient Name: ${patient.name || "N/A"}\n` +
          `Gender: ${patient.gender || "N/A"}\n` +
          `Phone: ${patient.phone || "N/A"}\n` +
          `Disease: ${patient.disease || "N/A"}\n` +
          `Status: ${patient.status || "N/A"}\n\n` +
          `Please check immediately.`
      );
    }
  }, [patient]);

  /* ---------------- RESET WHEN CLOSED ---------------- */
  useEffect(() => {
    if (!show) {
      setSelectedDoctor(null);
      setMessage("");
      setLoading(false);
    }
  }, [show]);

  /* ---------------- DOCTOR OPTIONS ---------------- */
  const doctorOptions = doctors.map((doc) => ({
    value: doc.doctor_id || doc.id,
    label: doc.doctor_name || doc.name || "Unknown Doctor",
    specialization: doc.specialization || "General",
  }));

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  );

  /* ---------------- SEND EMERGENCY ---------------- */
  const handleSendEmergency = async () => {
    if (!selectedDoctor) {
      alert("Please select a doctor");
      return;
    }

    if (!patient?.id) {
      alert("Patient information missing");
      return;
    }

    const emergencyData = {
      patient_id: patient.id,  
      doctor_id: selectedDoctor.value,
      message: message?.trim() || "Emergency alert for patient",
    };

    console.log("🚨 Emergency Payload:", emergencyData);

    try {
      setLoading(true);
      const res = await dispatch(markEmergencyAction(emergencyData));
      console.log("✅ Emergency marked:", res);
      if (onSubmit) onSubmit(emergencyData);
      onClose();
    } catch (error) {
      console.error("❌ Emergency error:", error);
      alert("Failed to mark emergency");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- HELPER: Get currently assigned doctor info ---------------- */
  const getCurrentDoctorDisplay = () => {
    const doctorId = patient?.doctor_id || patient?.emergencyMark_doctor_id;
    if (!doctorId || doctorId === "0") return null;

    const matched = doctors.find(doc => String(doc.doctor_id || doc.id) === String(doctorId));
    if (matched) {
      return { id: doctorId, name: matched.doctor_name || matched.name };
    }
    return { id: doctorId, name: null };
  };

  const currentDoctor = getCurrentDoctorDisplay();

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="bg-danger text-white">
        <Modal.Title>🚨 Emergency Alert</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ✅ NEW: Show currently assigned doctor (if any) */}
      

        {/* DOCTOR SELECT */}
        <label className="form-label fw-bold">
          Select Doctor <span className="text-danger">*</span>
        </label>

        <Select
          value={selectedDoctor}
          onChange={setSelectedDoctor}
          options={doctorOptions}
          formatOptionLabel={formatOptionLabel}
          placeholder="Search doctor..."
          isClearable
          isSearchable
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          isDisabled={loading}
        />

        {/* MESSAGE */}
        <label className="form-label fw-bold mt-3">Emergency Message</label>
        <textarea
          className="form-control"
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={loading}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleSendEmergency}
          disabled={loading || !selectedDoctor}
        >
          {loading ? "Sending..." : "Mark as Emergency"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmergencyModal;